<script lang="ts">
  import type { LeafletMouseEvent, Map as LeafletMap } from 'leaflet';
  import { SvelteURLSearchParams } from 'svelte/reactivity';
  import * as valibot from 'valibot';
  import DeparturesTab from './components/departures/departures-tab.svelte';
  import Gate from './components/gate.svelte';
  import Layout from './components/layout.svelte';
  import MapView from './components/map.svelte';
  import RouteTab from './components/route/route-tab.svelte';
  import TabBar, { type Tab } from './components/tab-bar.svelte';
  import Toast from './components/toast.svelte';
  import { getHafasResolve, getLines, resolveByStopId } from './lib/api';
  import type { Endpoint } from './lib/endpoint';
  import { formatLatLon } from './lib/endpoint';
  import { PASSWORD_HASH } from './lib/gate';
  import {
    composeRouteSignature,
    endpointSignature,
    loadCachedRouteFor,
    saveLastRoute,
  } from './lib/last-route';
  import { DeparturesResponseStore } from './stores/departures-response.svelte';
  import { EndpointsStore } from './stores/endpoints.svelte';
  import { ExcludeStore } from './stores/exclude.svelte';
  import { FavoritesStore } from './stores/favorites.svelte';
  import { RouteResponseStore } from './stores/route-response.svelte';
  import { TabStore } from './stores/tab.svelte';
  import { TimeStore } from './stores/time.svelte';
  import { toast } from './stores/toast.svelte';
  import {
    encodeDeparturesUrl,
    parseDeparturesUrl,
    type DeparturesEndpoint,
  } from './lib/url-state/departures';
  import { encodeRouteUrl, parseRouteUrl } from './lib/url-state/route';
  import type { RouteResponse } from './types/RouteResponse';
  import type { StopJson } from './types/StopJson';

  const RouteResponseShapeSchema = valibot.object({
    journeys: valibot.array(valibot.unknown()),
    realtime: valibot.object({ is_stale: valibot.boolean() }),
  });

  /** Rune-tracked slot for the currently-armed map picker, if any. */
  const mapPickerState = $state<{ target: 'origin' | 'destination' | undefined }>({
    target: undefined,
  });
  /** Non-reactive holder for the Leaflet map instance the main-page MapView exposes. */
  const mainMapHolder: { map: LeafletMap | undefined } = { map: undefined };

  const endpoints = new EndpointsStore();
  const time = new TimeStore();
  const route = new RouteResponseStore();
  const departures = new DeparturesResponseStore();
  const favorites = new FavoritesStore();
  const exclude = new ExcludeStore();
  const tabs = new TabStore();

  const TAB_ITEMS: Tab[] = [
    { key: 'route', label: 'Route' },
    { key: 'departures', label: 'Departures' },
  ];

  const COORD_DECIMALS = 5;

  /** Composite signature of the current search (both endpoints + chip block-params), or undefined when either endpoint is half-formed. */
  function currentSignature(): string | undefined {
    const block = exclude.toBlockParams();
    return composeRouteSignature(
      endpointSignature(endpoints.origin),
      endpointSignature(endpoints.dest),
      `${block.modes}|${block.lines}`,
    );
  }

  /** Human-readable label for `endpoint`; `"Stop N"` for stop endpoints, address text as-is, position formatted to 5 decimals. */
  function labelForEndpoint(endpoint: Endpoint): string {
    if (endpoint.kind === 'address') return endpoint.address;
    if (endpoint.kind === 'position') {
      return `${endpoint.latitude.toFixed(COORD_DECIMALS)}, ${endpoint.longitude.toFixed(COORD_DECIMALS)}`;
    }
    return `Stop ${String(endpoint.stopId)}`;
  }

  /**
   * Best-effort HAFAS-name lookup for a stop endpoint; returns undefined on
   * empty results, missing name, or any network failure. Failures also toast
   * so the user sees why their `Stop 12345` placeholder never hydrated.
   */
  async function hydrateStopLabel(
    endpoint: Extract<Endpoint, { kind: 'stop' }>,
  ): Promise<string | undefined> {
    try {
      const response = await resolveByStopId(endpoint.stopId);
      return response.results[0]?.name ?? undefined;
    } catch (error: unknown) {
      toast.showError('Stop label lookup', error);
      return undefined;
    }
  }

  /** True when `value` matches the {@link RouteResponse} shape the last-route cache validates against. */
  function isRouteResponseShape(value: unknown): value is RouteResponse {
    return valibot.is(RouteResponseShapeSchema, value);
  }

  /** Applies `routeState` (parsed URL) onto the endpoints/time/exclude stores; nullish fields leave their stores untouched. */
  function restoreEndpoints(routeState: ReturnType<typeof parseRouteUrl>): void {
    if (routeState.origin) {
      endpoints.setOrigin(routeState.origin, labelForEndpoint(routeState.origin));
    }
    if (routeState.dest) {
      endpoints.setDest(routeState.dest, labelForEndpoint(routeState.dest));
    }
    if (routeState.time) time.setFixed(routeState.time);
    exclude.restoreFromUrl(routeState.blockModes, routeState.blockLines);
  }

  /** Hydrates the route store from the last-route cache when the current signature matches; silently ignores a corrupted cache. */
  function restoreCachedRoute(): void {
    const signature = currentSignature();
    try {
      const cached: unknown = loadCachedRouteFor(localStorage, signature);
      if (isRouteResponseShape(cached)) {
        route.setSuccess(cached.journeys, cached.realtime);
      }
    } catch {
      /* garbage cached payload — leave route idle; a fresh search re-populates */
    }
  }

  /** Runs once at mount: parses `location.search`, restores tab + endpoints + cached route, and kicks off async label / departures hydration. */
  function hydrateFromUrlAndCache(): void {
    const parameters = new URLSearchParams(location.search);
    const rawTab = parameters.get('tab');
    if (rawTab !== null) tabs.activate(rawTab);
    const routeState = parseRouteUrl(parameters);
    restoreEndpoints(routeState);
    void hydrateLineCatalog();
    restoreCachedRoute();
    void refineStopLabels();
    void restoreDepartures(parseDeparturesUrl(parameters).endpoint);
  }

  /** Loads the departures board for an `ext_id` endpoint, defaulting missing coords / name to NaN / empty string. */
  async function loadDeparturesByExtensionId(
    endpoint: Extract<DeparturesEndpoint, { kind: 'ext_id' }>,
  ): Promise<void> {
    await departures.load({
      extId: endpoint.extId,
      name: endpoint.name ?? '',
      lat: endpoint.latitude ?? NaN,
      lon: endpoint.longitude ?? NaN,
    });
  }

  /**
   * Resolves `query` via HAFAS name search then loads the first match. Empty
   * input / no results silently fall through to the tab's empty-state prompt;
   * genuine fetch failures toast so the user sees the network / API problem.
   */
  async function loadDeparturesByQuery(query: string): Promise<void> {
    if (!query) return;
    try {
      const response = await getHafasResolve(query);
      if (response.results.length === 0) return;
      const first = response.results[0];
      await departures.load({
        extId: first.ext_id,
        name: first.name,
        lat: first.lat,
        lon: first.lon,
      });
    } catch (error: unknown) {
      toast.showError('Departures search', error);
    }
  }

  /** Dispatches URL-restored departures endpoints — `ext_id` loads directly, `position`/`query` fall back to HAFAS name search. */
  async function restoreDepartures(
    endpoint: ReturnType<typeof parseDeparturesUrl>['endpoint'],
  ): Promise<void> {
    if (!endpoint) return;
    if (endpoint.kind === 'ext_id') {
      await loadDeparturesByExtensionId(endpoint);
      return;
    }
    // `position` and `query` don't carry an ext_id — resolve via HAFAS name search.
    const query = endpoint.kind === 'query' ? endpoint.query : (endpoint.name ?? '');
    await loadDeparturesByQuery(query);
  }

  /**
   * Fetches the line catalog then feeds it into the exclude store. Toasts on
   * failure — line-chip suggestions silently degrade but the user now sees
   * why they never got line suggestions on the exclude widget.
   */
  async function hydrateLineCatalog(): Promise<void> {
    try {
      const catalog = await getLines();
      exclude.setCatalog(catalog);
      // Backfill mode on chips the URL restore inserted before the catalog
      // was available — additive, preserves any chip edits the user made.
      exclude.backfillLineModes(catalog);
    } catch (error: unknown) {
      toast.showError('Line catalog', error);
    }
  }

  interface EndpointSnapshot {
    origin: Endpoint | undefined;
    dest: Endpoint | undefined;
    originLabel: string;
    destinationLabel: string;
  }

  /** A pre-resolved `Promise<undefined>` used as the "no lookup needed" slot in {@link fetchRefinedLabels}. */
  function makeAbsentLabel(): Promise<string | undefined> {
    const empty: string | undefined = undefined;
    return Promise.resolve(empty);
  }
  const ABSENT_LABEL = makeAbsentLabel();

  /** Fires both stop-label lookups in parallel; non-stop endpoints slot in the pre-resolved {@link ABSENT_LABEL}. */
  async function fetchRefinedLabels(
    snapshot: EndpointSnapshot,
  ): Promise<[string | undefined, string | undefined]> {
    return Promise.all([
      snapshot.origin?.kind === 'stop' ? hydrateStopLabel(snapshot.origin) : ABSENT_LABEL,
      snapshot.dest?.kind === 'stop' ? hydrateStopLabel(snapshot.dest) : ABSENT_LABEL,
    ]);
  }

  /** Writes `name` onto `endpoints.originLabel` only if the endpoint identity and label are still the snapshot's — no-op after the user changed either. */
  function applyOriginLabel(name: string, snapshot: EndpointSnapshot): void {
    if (endpoints.origin !== snapshot.origin) return;
    if (endpoints.originLabel !== snapshot.originLabel) return;
    endpoints.originLabel = name;
  }

  /** Same guarded write as {@link applyOriginLabel}, but for the destination side. */
  function applyDestinationLabel(name: string, snapshot: EndpointSnapshot): void {
    if (endpoints.dest !== snapshot.dest) return;
    if (endpoints.destLabel !== snapshot.destinationLabel) return;
    endpoints.destLabel = name;
  }

  /** Kicks off HAFAS lookups for both stop endpoints then writes the resolved names back — race-safe (skips writes when the user moved on). */
  async function refineStopLabels(): Promise<void> {
    const snapshot: EndpointSnapshot = {
      origin: endpoints.origin,
      dest: endpoints.dest,
      originLabel: endpoints.originLabel,
      destinationLabel: endpoints.destLabel,
    };
    const [originName, destinationName] = await fetchRefinedLabels(snapshot);
    if (originName !== undefined) applyOriginLabel(originName, snapshot);
    if (destinationName !== undefined) applyDestinationLabel(destinationName, snapshot);
  }

  /**
   * URL keys this app owns. `writeUrl` wipes only these before writing fresh
   * state, so foreign params (utm_source, feature flags, debug toggles) survive.
   */
  const APP_URL_KEYS: ReadonlySet<string> = new Set([
    'from',
    'from_stop',
    'from_address',
    'to',
    'to_stop',
    'to_address',
    'time',
    'block_lines',
    'block_modes',
    'dep_ext_id',
    'dep_name',
    'dep_lat',
    'dep_lon',
    'dep_q',
    'tab',
  ]);

  /** Encodes the current route-tab state as its URL query params (via {@link encodeRouteUrl}); time defaults to undefined when unset. */
  function encodeRouteQuery(): URLSearchParams {
    const block = exclude.toBlockParams();
    return encodeRouteUrl({
      origin: endpoints.origin,
      dest: endpoints.dest,
      time: time.toUrlValue() ?? undefined,
      blockLines: block.lines,
      blockModes: block.modes,
    });
  }

  /** Builds a `{ kind: 'ext_id' }` endpoint from the loaded departures stop; throws when no stop is selected (caller-guarded). */
  function encodeDeparturesEndpointFromStop(): DeparturesEndpoint {
    const stop = departures.stop;
    /* istanbul ignore if -- guarded by caller before invocation */
    if (!stop) throw new Error('no departures stop');
    return {
      kind: 'ext_id',
      extId: stop.extId,
      name: stop.name || undefined,
      latitude: Number.isFinite(stop.lat) ? stop.lat : undefined,
      longitude: Number.isFinite(stop.lon) ? stop.lon : undefined,
    };
  }

  /** Combined URL params for the whole app (route + tab + departures); folds departures params in only when a stop is loaded. */
  function encodeAppQuery(): URLSearchParams {
    const appParameters = encodeRouteQuery();
    if (tabs.active !== 'route') appParameters.set('tab', tabs.active);
    if (departures.stop) {
      const departuresParameters = encodeDeparturesUrl({
        endpoint: encodeDeparturesEndpointFromStop(),
      });
      for (const [parameterKey, parameterValue] of departuresParameters)
        appParameters.set(parameterKey, parameterValue);
    }
    return appParameters;
  }

  /** True when `parameterKey` is one this app owns (`APP_URL_KEYS`) — used to drop stale app params in a single pass over the URL. */
  function isAppOwnedParameter(parameterKey: string): boolean {
    return APP_URL_KEYS.has(parameterKey);
  }

  /** Copies every non-app-owned URL param from `location.search` into `target`, preserving foreign params (utm, feature flags, etc.). */
  function mergeForeignParameters(target: SvelteURLSearchParams): void {
    const currentParameters = new URLSearchParams(location.search);
    for (const [parameterKey, parameterValue] of currentParameters) {
      if (isAppOwnedParameter(parameterKey)) continue;
      target.set(parameterKey, parameterValue);
    }
  }

  /** Rebuilds the URL: foreign params first (preserved), then fresh app params; empty search collapses to the pathname. */
  function writeUrl(): void {
    const appParameters = encodeAppQuery();
    const merged = new SvelteURLSearchParams();
    mergeForeignParameters(merged);
    for (const [parameterKey, parameterValue] of appParameters)
      merged.set(parameterKey, parameterValue);
    const search = merged.toString();
    const next = search ? `?${search}` : location.pathname;
    history.replaceState(null, '', next);
  }

  /** Post-search hook: rewrites the URL and caches the response under the current signature (only when `route.freshness` is populated). */
  function onAfterSearch(): void {
    writeUrl();
    if (route.freshness) {
      saveLastRoute(localStorage, currentSignature(), {
        journeys: route.journeys,
        realtime: route.freshness,
      });
    }
  }

  /**
   * Chip mutations change the signature but leave `route.journeys` pointing
   * at the pre-mutation results — never save under the new signature or the
   * cache will hand back the wrong-chip journeys on next reload.
   */
  function onChipChange(): void {
    writeUrl();
  }

  /**
   * Swap flips origin/dest; the URL must follow so Copy link and reload
   * reflect the new direction. `route.journeys` stays pre-swap-directional,
   * so the cache is intentionally left alone (same reasoning as {@link onChipChange}).
   */
  function onAfterSwap(): void {
    writeUrl();
  }

  /** Post-departures-change hook: rewrites the URL so a copied link restores the same stop / query. */
  function onAfterDeparturesChange(): void {
    writeUrl();
  }

  /** Directly loads the departures board for a journey stop that already carries its HAFAS ext_id. */
  async function loadDeparturesByStop(stop: StopJson & { ext_id: string }): Promise<void> {
    await departures.load({ extId: stop.ext_id, name: stop.name, lat: stop.lat, lon: stop.lon });
  }

  /** Click-from-journey handler: loads the board (direct via ext_id, or name-search fallback), then
   * switches to Departures and rewrites the URL. Load failures leave the current tab alone. */
  async function openStopDepartures(stop: StopJson): Promise<void> {
    if (stop.ext_id) {
      await loadDeparturesByStop({ ...stop, ext_id: stop.ext_id });
    } else {
      await loadDeparturesByQuery(stop.name);
    }
    tabs.activate('departures');
    writeUrl();
  }

  /** Void wrapper so the callback conforms to `(stop) => void` without leaking the async return. */
  function onOpenStopDepartures(stop: StopJson): void {
    void openStopDepartures(stop);
  }

  /** Tab switch: activate the tab in the store, then reflect it in the URL. */
  function onSelectTab(tabKey: string): void {
    tabs.activate(tabKey);
    writeUrl();
  }

  // Runs once on mount: URL params (tab + per-tab state) + last-route cache.
  hydrateFromUrlAndCache();

  /** Applies a picked coord to whichever endpoint is currently armed; disarms the picker. */
  function applyPickedCoord(latitude: number, longitude: number): void {
    const target = mapPickerState.target;
    if (!target) return;
    const label = formatLatLon(latitude, longitude);
    const endpoint: Endpoint = { kind: 'position', latitude, longitude };
    if (target === 'origin') endpoints.setOrigin(endpoint, label);
    else endpoints.setDest(endpoint, label);
    mapPickerState.target = undefined;
    writeUrl();
  }

  /** Handles a click on the main-page Leaflet map; runs the armed picker or no-ops. */
  function handleMainMapClick(event: LeafletMouseEvent): void {
    applyPickedCoord(event.latlng.lat, event.latlng.lng);
  }

  /** Captures the map instance and hooks the picker click handler. */
  function handleMainMapReady(mapInstance: LeafletMap): void {
    mainMapHolder.map = mapInstance;
    mapInstance.on('click', handleMainMapClick);
  }

  /** Clears the cached map reference when MapView unmounts (e.g. Departures tab switch). */
  function handleMainMapDestroy(): void {
    const previous = mainMapHolder.map;
    /* istanbul ignore else -- onDestroy fires from Layout's unmount, always after handleMainMapReady set the holder. */
    if (previous) previous.off('click', handleMainMapClick);
    mainMapHolder.map = undefined;
    mapPickerState.target = undefined;
  }

  /** Arms the picker to fill the origin on the next map click. */
  function armOriginPicker(): void {
    mapPickerState.target = 'origin';
  }

  /** Arms the picker to fill the destination on the next map click. */
  function armDestinationPicker(): void {
    mapPickerState.target = 'destination';
  }

  $effect(() => {
    const target = mapPickerState.target;
    const mapInstance = mainMapHolder.map;
    /* istanbul ignore if -- effect runs after mount; map is ready by the time picker is armed. */
    if (!mapInstance) return;
    mapInstance.getContainer().style.cursor = target ? 'crosshair' : '';
  });
</script>

<Toast store={toast} />
<Gate passwordHash={PASSWORD_HASH}>
  <Layout showMap={tabs.active === 'route'}>
    {#snippet panel()}
      <TabBar tabs={TAB_ITEMS} active={tabs.active} onSelect={onSelectTab} />
      {#if tabs.active === 'route'}
        <RouteTab
          {endpoints}
          {time}
          {route}
          {exclude}
          {onAfterSearch}
          {onChipChange}
          {onAfterSwap}
          {onOpenStopDepartures}
          onPickOriginFromMap={armOriginPicker}
          onPickDestFromMap={armDestinationPicker}
          mapPickerTarget={mapPickerState.target}
        />
      {:else}
        <DeparturesTab {departures} {favorites} onAfterChange={onAfterDeparturesChange} />
      {/if}
    {/snippet}
    {#snippet map()}
      <MapView
        journey={route.selectedJourney}
        onReady={handleMainMapReady}
        onDestroy={handleMainMapDestroy}
      />
    {/snippet}
  </Layout>
</Gate>
